import { FC, useCallback, useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import LegacyForm from 'components/Form';
import { logEvent } from 'features/Analytics/analytics';
import SelectContactsToSendTo from 'features/Work/components/SelectContactsToSendTo';

import useEmailReminderFormState from '../../hooks/useEmailReminderFormState';
import useSendDummyInvoiceReminderToUser from '../../hooks/useSendDummyInvoiceReminderToUser';
import useUpdateInvoiceReminderSettings from '../../hooks/useUpdateInvoiceReminderSettings';
import { SidebarProps } from '../../types';
import { getMaxOccurrencesToSave } from '../../utils';
import locales from './locales';

const REMINDER_FREQUENCIES = [
  {
    label: locales.once,
    value: 'ONCE',
  },
  {
    label: locales.givenWeeks,
    value: 'FOR_GIVEN_WEEKS',
  },
  {
    label: locales.untilPayment,
    value: 'EVERY_WEEK_UNTIL_PAYMENT',
  },
];

export const EmailReminder: FC<SidebarProps> = ({ client, invoice }) => {
  const [settingsSaved, setSettingsSaved] = useState(false);
  const { clientId, invoiceId, settings } = invoice;
  const {
    formDataHasChanged,
    isFormInvalid,
    isGivenWeeksFrequency,
    onChange,
    state,
  } = useEmailReminderFormState({ client, invoice });

  const {
    loading: isSendingDummyInvoiceReminderToUser,
    onSendDummyInvoiceReminderToUser,
  } = useSendDummyInvoiceReminderToUser({ invoiceId });
  const [updateInvoiceReminderSettings, { loading: isUpdating }] =
    useUpdateInvoiceReminderSettings();

  const onSubmitForm = useCallback(async () => {
    if (isFormInvalid) {
      return;
    }
    const {
      clientContactIds,
      isReminderEnabled,
      isUserCcedOnEmails,
      reminderFrequency,
      reminderMaxOccurrences,
    } = state;
    const isOnlyOnce = reminderFrequency === 'ONCE';

    try {
      await updateInvoiceReminderSettings({
        variables: {
          input: {
            clientContactIds,
            invoiceId,
            isUserCcedOnEmails,
            settings: {
              isReminderEnabled,
              reminderFrequency: isOnlyOnce ? 'ONCE' : 'WEEKLY',
              reminderMaxOccurrences: getMaxOccurrencesToSave(
                reminderFrequency,
                reminderMaxOccurrences,
              ),
            },
          },
        },
      });
      setSettingsSaved(true);
      logEvent({
        name: 'Invoice Email Reminder Created',
        properties: { invoiceId },
      });
    } catch (_) {
      // Error already handled by the hook
    }
  }, [isFormInvalid, invoiceId, state, updateInvoiceReminderSettings]);

  useEffect(() => {
    if (formDataHasChanged) {
      setSettingsSaved(false);
    }
  }, [formDataHasChanged]);

  useEffect(() => {
    // Save isReminderEnabled directly on toggle on/off
    // On toggle off reminder
    if (!state.isReminderEnabled && settings.isReminderEnabled) {
      updateInvoiceReminderSettings({
        variables: {
          input: {
            invoiceId,
            settings: { isReminderEnabled: state.isReminderEnabled },
          },
        },
      });
    }
    // On toggle on reminder
    if (state.isReminderEnabled && !settings.isReminderEnabled) {
      onSubmitForm();
    }
  }, [
    invoiceId,
    onSubmitForm,
    settings.isReminderEnabled,
    state.isReminderEnabled,
    updateInvoiceReminderSettings,
  ]);

  return (
    <>
      <SunshineCard
        isChecked={state.isReminderEnabled}
        onChange={() => onChange('isReminderEnabled')(!state.isReminderEnabled)}
        title={locales.optionTitle}
        variant="checkbox"
      />

      {state.isReminderEnabled ? (
        <LegacyForm onValidSubmit={onSubmitForm}>
          <Box>
            <Button
              icon="mail"
              isDisabled={isSendingDummyInvoiceReminderToUser}
              marginBottom="space-32"
              onClick={() => onSendDummyInvoiceReminderToUser()}
              variant="inline-secondary"
            >
              {locales.sendToMyself}
            </Button>

            <Text
              fontWeight="weight-500"
              marginBottom="space-8"
              variant="primary"
            >
              <SunshineIcon
                marginRight="space-8"
                name="profile"
                size="icon-16"
              />
              {locales.contacts}
            </Text>

            <Box marginBottom="space-24">
              <SelectContactsToSendTo
                checkboxId="cc-user-invoice-reminder"
                checked={state.isUserCcedOnEmails}
                clientId={clientId}
                onChangeCheckbox={onChange('isUserCcedOnEmails')}
                onChangeSelect={onChange('clientContactIds')}
                requiredErrorMessage={locales.requiredContactsError}
                selectValue={state.clientContactIds}
              />
            </Box>

            <Text
              fontWeight="weight-500"
              marginBottom="space-8"
              variant="primary"
            >
              <SunshineIcon marginRight="space-8" name="timer" size="icon-16" />
              {locales.frequency}
            </Text>

            <Form.Field
              label={locales.occurence}
              marginBottom={isGivenWeeksFrequency ? 'space-8' : 'space-24'}
            >
              <Form.SunshineSelect
                isDisabled={isUpdating}
                onChange={(option) =>
                  onChange('reminderFrequency')(option?.value ?? null)
                }
                options={REMINDER_FREQUENCIES}
                value={REMINDER_FREQUENCIES.find(
                  (option) => option.value === state.reminderFrequency,
                )}
              />
            </Form.Field>

            {isGivenWeeksFrequency ? (
              <Form.Field
                error={
                  state.reminderMaxOccurrences
                    ? undefined
                    : locales.emptyMaxOccurenceError
                }
                label={locales.totalWeeksLabel}
                marginBottom="space-24"
              >
                <Form.Input
                  isDisabled={isUpdating}
                  onChange={(e) =>
                    onChange('reminderMaxOccurrences')(e.target.valueAsNumber)
                  }
                  type="number"
                  value={state.reminderMaxOccurrences}
                />
              </Form.Field>
            ) : null}
          </Box>

          <Button
            isDisabled={isFormInvalid || settingsSaved}
            isLoading={isUpdating}
            type="submit"
            variant="primary"
            width="100%"
          >
            {settingsSaved ? locales.settingsSaved : locales.save}
          </Button>
        </LegacyForm>
      ) : null}
    </>
  );
};
